/**
 * FloatingDock — Unified floating command dock for Phone + AI Assistant.
 *
 * Replaces the clashing separate bubbles with a single gorgeous dock
 * that holds both actions in a pill-shaped container with:
 *   - Glassmorphism backdrop
 *   - Spring-physics animations
 *   - Magnetic hover effects
 *   - Contextual status indicators
 *   - Smooth panel transitions
 */
import { useState, useCallback, useRef, useEffect } from 'react'
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform } from 'framer-motion'
import { Phone, PhoneCall, PhoneIncoming, Sparkles, X, MessageSquare } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useSoftphone, type SoftphoneStatus } from '@/hooks/useSoftphone'

// ── Types ───────────────────────────────────────────────────────

type ActivePanel = 'none' | 'phone' | 'ai'

interface FloatingDockProps {
  onToggleAi: (open: boolean) => void
  isAiOpen: boolean
}

// ── Status config ───────────────────────────────────────────────

const PHONE_STATUS: Record<SoftphoneStatus, { color: string; glow: string; pulse: boolean; label: string }> = {
  disconnected: { color: 'from-zinc-400 to-zinc-500', glow: 'shadow-zinc-400/0', pulse: false, label: 'Offline' },
  connecting:   { color: 'from-amber-400 to-orange-500', glow: 'shadow-amber-400/40', pulse: true, label: 'Connecting' },
  ready:        { color: 'from-emerald-400 to-emerald-600', glow: 'shadow-emerald-500/40', pulse: false, label: 'Online' },
  ringing:      { color: 'from-green-400 to-green-600', glow: 'shadow-green-500/50', pulse: true, label: 'Ringing' },
  'on-call':    { color: 'from-blue-400 to-blue-600', glow: 'shadow-blue-500/40', pulse: true, label: 'On Call' },
  error:        { color: 'from-red-400 to-red-600', glow: 'shadow-red-500/40', pulse: false, label: 'Error' },
}

// ── Dock Item ───────────────────────────────────────────────────

interface DockItemProps {
  icon: React.ReactNode
  label: string
  isActive: boolean
  onClick: () => void
  badge?: React.ReactNode
  glowColor?: string
  mouseX: ReturnType<typeof useMotionValue<number>>
  index: number
}

function DockItem({ icon, label, isActive, onClick, badge, glowColor, mouseX, index }: DockItemProps) {
  const ref = useRef<HTMLButtonElement>(null)

  const distance = useTransform(mouseX, (val: number) => {
    const rect = ref.current?.getBoundingClientRect()
    if (!rect) return 150
    return val - rect.x - rect.width / 2
  })

  const size = useSpring(
    useTransform(distance, [-100, 0, 100], [48, 56, 48]),
    { mass: 0.1, stiffness: 200, damping: 15 }
  )

  const y = useSpring(
    useTransform(distance, [-100, 0, 100], [0, -6, 0]),
    { mass: 0.1, stiffness: 200, damping: 15 }
  )

  return (
    <motion.div className="relative flex flex-col items-center" style={{ y }}>
      {/* Tooltip */}
      <AnimatePresence>
        {isActive && (
          <motion.div
            initial={{ opacity: 0, y: 4, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 4, scale: 0.9 }}
            className="absolute -top-8 whitespace-nowrap rounded-md bg-foreground/90 px-2 py-0.5 text-[10px] font-medium text-background backdrop-blur-sm"
          >
            {label}
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        ref={ref}
        onClick={onClick}
        style={{ width: size, height: size }}
        whileTap={{ scale: 0.9 }}
        className={cn(
          'relative flex items-center justify-center rounded-2xl transition-colors duration-200',
          isActive
            ? 'bg-linear-to-br from-primary to-primary/80 text-primary-foreground shadow-lg'
            : 'bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground',
          glowColor && isActive && `shadow-lg ${glowColor}`,
        )}
        aria-label={label}
      >
        {icon}
        {badge}
      </motion.button>

      {/* Active indicator dot */}
      <motion.div
        initial={false}
        animate={{
          scale: isActive ? 1 : 0,
          opacity: isActive ? 1 : 0,
        }}
        className="mt-1 h-1 w-1 rounded-full bg-primary"
      />
    </motion.div>
  )
}

// ── Main Dock ───────────────────────────────────────────────────

export function FloatingDock({ onToggleAi, isAiOpen }: FloatingDockProps) {
  const sp = useSoftphone()
  const phoneConfig = PHONE_STATUS[sp.status]
  const mouseX = useMotionValue(Infinity)
  const [hovered, setHovered] = useState(false)

  const handlePhoneClick = useCallback(() => {
    // Dispatch a custom event that the Softphone listens to
    window.dispatchEvent(new CustomEvent('softphone:toggle'))
  }, [])

  const handleAiClick = useCallback(() => {
    onToggleAi(!isAiOpen)
  }, [onToggleAi, isAiOpen])

  // Phone icon based on status
  const phoneIcon = sp.status === 'ringing'
    ? <PhoneIncoming className="h-5 w-5" />
    : sp.status === 'on-call'
      ? <PhoneCall className="h-5 w-5" />
      : <Phone className="h-5 w-5" />

  // Phone badge (status dot)
  const phoneBadge = (
    <span className={cn(
      'absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-background',
      sp.status === 'ready' ? 'bg-emerald-500' :
      sp.status === 'ringing' ? 'bg-green-500 animate-ping' :
      sp.status === 'on-call' ? 'bg-blue-500' :
      sp.status === 'connecting' ? 'bg-amber-400 animate-pulse' :
      sp.status === 'error' ? 'bg-red-500' :
      'bg-zinc-400',
    )} />
  )

  // Call duration badge
  const callBadge = sp.status === 'on-call' ? (
    <motion.span
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      className="absolute -top-2 -right-3 rounded-full bg-blue-600 px-1.5 py-0.5 text-[9px] font-mono font-bold text-white shadow-md"
    >
      {Math.floor(sp.callDuration / 60)}:{String(sp.callDuration % 60).padStart(2, '0')}
    </motion.span>
  ) : phoneBadge

  return (
    <motion.div
      initial={{ opacity: 0, y: 40, scale: 0.8 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ type: 'spring', damping: 20, stiffness: 200, delay: 0.3 }}
      className="fixed bottom-5 right-5 z-50"
    >
      {/* Ambient glow behind dock */}
      <div className={cn(
        'absolute inset-0 rounded-3xl blur-xl transition-all duration-700',
        hovered ? 'opacity-60' : 'opacity-30',
        sp.status === 'ringing' ? 'bg-green-500/30' :
        sp.status === 'on-call' ? 'bg-blue-500/30' :
        isAiOpen ? 'bg-primary/20' :
        'bg-muted-foreground/10',
      )} />

      {/* Dock container */}
      <motion.div
        onMouseMove={(e) => mouseX.set(e.pageX)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => { mouseX.set(Infinity); setHovered(false) }}
        layout
        className={cn(
          'relative flex items-end gap-2 rounded-2xl px-3 py-2.5',
          'border border-border/40',
          'bg-background/80 backdrop-blur-xl backdrop-saturate-150',
          'shadow-xl shadow-black/10',
          'dark:bg-background/60 dark:border-border/30',
        )}
      >
        {/* Ringing shimmer overlay */}
        <AnimatePresence>
          {sp.status === 'ringing' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none"
            >
              <motion.div
                animate={{ x: ['-100%', '200%'] }}
                transition={{ repeat: Infinity, duration: 1.5, ease: 'linear' }}
                className="absolute inset-y-0 w-1/3 bg-linear-to-r from-transparent via-green-400/20 to-transparent"
              />
            </motion.div>
          )}
        </AnimatePresence>

        <DockItem
          icon={phoneIcon}
          label={phoneConfig.label}
          isActive={sp.status !== 'disconnected'}
          onClick={handlePhoneClick}
          badge={callBadge}
          glowColor={phoneConfig.glow}
          mouseX={mouseX}
          index={0}
        />

        {/* Divider */}
        <div className="mx-0.5 h-8 w-px bg-border/40 self-center" />

        <DockItem
          icon={isAiOpen ? <X className="h-5 w-5" /> : <Sparkles className="h-5 w-5" />}
          label={isAiOpen ? 'Close AI assistant' : 'Open AI assistant'}
          isActive={isAiOpen}
          onClick={handleAiClick}
          mouseX={mouseX}
          index={1}
        />
      </motion.div>
    </motion.div>
  )
}
